import React, { useState, useEffect } from 'react';
import { 
  Calendar,
  Phone, MapPin, User, Settings, Filter
} from 'lucide-react';
import api from 'services/api';

const AgendamentosOS = () => {
  const [loading, setLoading] = useState(true);
  const [osAbertas, setOsAbertas] = useState([]);
  const [tecnicosDisponiveis, setTecnicosDisponiveis] = useState([]);
  const [filtros, setFiltros] = useState({
    prioridade: 'todas',
    regiao: 'todas'
  });
  const [modalAgendamento, setModalAgendamento] = useState(null);
  const [agendamentoForm, setAgendamentoForm] = useState({
    tecnicoId: '',
    dataAgendamento: '',
    horaAgendamento: '',
    observacoes: ''
  });

  const carregarDadosAgendamento = async () => {
    setLoading(true);
    
    try {
      // Buscar OS abertas e técnicos disponíveis em paralelo
      const [responseOS, responseTecnicos] = await Promise.all([
        api.get('/operacional/os-para-agendamento', {
          params: {
            data_inicio: '2025-01-01',
            data_fim: '2025-12-31',
            status: 'aberto'
          }
        }),
        api.get('/operacional/tecnicos-disponiveis', {
          params: {
            data: new Date().toISOString().split('T')[0]
          }
        })
      ]);

      if (responseOS.data?.success) {
        setOsAbertas(responseOS.data.data.os_abertas || []);
      }

      if (responseTecnicos.data?.success) {
        setTecnicosDisponiveis(responseTecnicos.data.data || []);
      }
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados de agendamento:', error);
      
      // Em caso de erro, usar dados vazios
      setOsAbertas([]);
      setTecnicosDisponiveis([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDadosAgendamento();
  }, []);

  const abrirModalAgendamento = (os) => {
    setModalAgendamento(os);
    setAgendamentoForm({
      tecnicoId: '',
      dataAgendamento: new Date().toISOString().split('T')[0],
      horaAgendamento: '',
      observacoes: ''
    });
  };

  const fecharModalAgendamento = () => {
    setModalAgendamento(null);
    setAgendamentoForm({
      tecnicoId: '',
      dataAgendamento: '',
      horaAgendamento: '',
      observacoes: ''
    });
  };

  const confirmarAgendamento = async () => {
    if (!agendamentoForm.tecnicoId || !agendamentoForm.dataAgendamento || !agendamentoForm.horaAgendamento) {
      alert('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    try {
      const response = await api.post('/operacional/agendar-os', {
        os_id: modalAgendamento.id,
        tecnico_id: agendamentoForm.tecnicoId,
        data_agendamento: agendamentoForm.dataAgendamento,
        hora_agendamento: agendamentoForm.horaAgendamento,
        observacoes: agendamentoForm.observacoes
      });

      if (response.data?.success) {
        alert('OS agendada com sucesso!');
        fecharModalAgendamento();
        carregarDadosAgendamento();
      } else {
        alert('Erro ao agendar OS');
      }
    } catch (error) {
      console.error('Erro ao agendar OS:', error);
      alert('Erro ao agendar OS');
    }
  };

  const getPrioridadeColor = (prioridade) => {
    switch (prioridade?.toLowerCase()) {
      case 'crítica': return 'text-[color:var(--status-bad)] bg-[color:var(--surface-muted)]';
      case 'alta': return 'text-[color:var(--status-warn)] bg-[color:var(--surface-muted)]';
      case 'média': return 'text-[color:var(--status-warn)] bg-[color:var(--surface-muted)]';
      case 'baixa': return 'text-[color:var(--status-good)] bg-[color:var(--surface-muted)]';
      default: return 'text-[color:var(--text-muted)] bg-[color:var(--surface-muted)]';
    }
  };

  const osFiltradas = osAbertas.filter(os => {
    if (filtros.prioridade !== 'todas' && os.prioridade?.toLowerCase() !== filtros.prioridade) return false;
    if (filtros.regiao !== 'todas' && !os.endereco?.includes(filtros.regiao)) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[color:var(--border)] border-t-[color:var(--accent)] mx-auto"></div>
          <p className="mt-2 text-[color:var(--text-muted)]">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-4">
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-[color:var(--text-muted)]" />
          <span className="font-medium text-[color:var(--text-primary)]">Filtros:</span>
          
          <select
            value={filtros.prioridade}
            onChange={(e) => setFiltros({...filtros, prioridade: e.target.value})}
            className="px-3 py-1 border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] rounded-lg text-sm"
          >
            <option value="todas">Todas as Prioridades</option>
            <option value="crítica">Crítica</option>
            <option value="alta">Alta</option>
            <option value="média">Média</option>
            <option value="baixa">Baixa</option>
          </select>

          <select
            value={filtros.regiao}
            onChange={(e) => setFiltros({...filtros, regiao: e.target.value})}
            className="px-3 py-1 border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] rounded-lg text-sm"
          >
            <option value="todas">Todas as Regiões</option>
            <option value="Santarém">Santarém</option>
            <option value="Altamira">Altamira</option>
            <option value="Itaituba">Itaituba</option>
            <option value="Uruará">Uruará</option>
          </select>

          <button
            onClick={carregarDadosAgendamento}
            className="px-4 py-1 bg-[color:var(--accent)] text-white rounded-lg text-sm hover:opacity-90"
          >
            Atualizar
          </button>
        </div>
      </div>

      {/* Lista de OS para Agendamento */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)]">
        <div className="p-4 border-b border-[color:var(--border)]">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
            OS Abertas para Agendamento ({osFiltradas.length})
          </h3>
        </div>
        
        <div className="divide-y divide-[color:var(--border)]">
          {osFiltradas.map((os, index) => (
            <div key={index} className="p-4 hover:bg-[color:var(--surface-muted)]">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="font-bold text-[color:var(--text-primary)]">{os.id}</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPrioridadeColor(os.prioridade)}`}>
                      {os.prioridade}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <User className="h-4 w-4 text-[color:var(--text-muted)]" />
                        <span className="font-medium">{os.cliente}</span>
                      </div>
                      <div className="flex items-center space-x-2 mb-1">
                        <Phone className="h-4 w-4 text-[color:var(--text-muted)]" />
                        <span className="text-[color:var(--text-muted)]">{os.telefone}</span>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <MapPin className="h-4 w-4 text-[color:var(--text-muted)]" />
                        <span className="text-[color:var(--text-muted)]">{os.endereco}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Settings className="h-4 w-4 text-[color:var(--text-muted)]" />
                        <span className="text-[color:var(--text-muted)]">{os.tipo_servico}</span>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-[color:var(--text-muted)] mb-1">
                        <strong>Abertura:</strong> {new Date(os.data_abertura).toLocaleDateString('pt-BR')}
                      </p>
                      <p className="text-[color:var(--text-muted)] mb-1">
                        <strong>SLA:</strong> {new Date(os.prazo_sla).toLocaleDateString('pt-BR')}
                      </p>
                      {os.tecnico_agendado && (
                        <p className="text-[color:var(--status-good)] text-sm">
                          <strong>Agendado:</strong> {os.tecnico_agendado}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {os.observacoes && (
                    <p className="text-sm text-[color:var(--text-muted)] mt-2 italic">"{os.observacoes}"</p>
                  )}
                </div>
                
                <div className="ml-4">
                  {!os.tecnico_agendado ? (
                    <button
                      onClick={() => abrirModalAgendamento(os)}
                      className="flex items-center space-x-2 px-4 py-2 bg-[color:var(--accent)] text-white rounded-lg hover:opacity-90"
                    >
                      <Calendar className="h-4 w-4" />
                      <span>Agendar</span>
                    </button>
                  ) : (
                    <span className="px-4 py-2 bg-[color:var(--surface-muted)] text-[color:var(--status-good)] rounded-lg text-sm font-medium">
                      Agendado
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de Agendamento */}
      {modalAgendamento && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[color:var(--surface)] rounded-lg p-6 w-full max-w-md border border-[color:var(--border)]">
            <h3 className="text-lg font-semibold mb-4 text-[color:var(--text-primary)]">
              Agendar OS: {modalAgendamento.id}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-1">
                  Técnico *
                </label>
                <select
                  value={agendamentoForm.tecnicoId}
                  onChange={(e) => setAgendamentoForm({...agendamentoForm, tecnicoId: e.target.value})}
                  className="w-full px-3 py-2 border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)]"
                >
                  <option value="">Selecione um técnico</option>
                  {tecnicosDisponiveis
                    .filter(tec => tec.status === 'Disponível')
                    .map(tecnico => (
                      <option key={tecnico.id} value={tecnico.id}>
                        {tecnico.nome} - {tecnico.especialidade} ({tecnico.regiao})
                      </option>
                    ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-1">
                    Data *
                  </label>
                  <input
                    type="date"
                    value={agendamentoForm.dataAgendamento}
                    onChange={(e) => setAgendamentoForm({...agendamentoForm, dataAgendamento: e.target.value})}
                    className="w-full px-3 py-2 border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-1">
                    Hora *
                  </label>
                  <input
                    type="time"
                    value={agendamentoForm.horaAgendamento}
                    onChange={(e) => setAgendamentoForm({...agendamentoForm, horaAgendamento: e.target.value})}
                    className="w-full px-3 py-2 border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)]"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-1">
                  Observações
                </label>
                <textarea
                  value={agendamentoForm.observacoes}
                  onChange={(e) => setAgendamentoForm({...agendamentoForm, observacoes: e.target.value})}
                  className="w-full px-3 py-2 border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] text-[color:var(--text-primary)]"
                  rows="3"
                  placeholder="Observações adicionais..."
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={confirmarAgendamento}
                className="flex-1 bg-[color:var(--accent)] text-white py-2 rounded-lg hover:opacity-90"
              >
                Confirmar Agendamento
              </button>
              <button
                onClick={fecharModalAgendamento}
                className="flex-1 bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] py-2 rounded-lg hover:bg-[color:var(--surface)]"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgendamentosOS;

